

































































































































































































import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch,
  vue3Set,
  defineVue3AsyncComponent
} from '~/utils/nuxt3-migration'
import { CartSellerInfo } from '~/models/marketplace/cart'
import CTab from '~/components/shared/configurable/tab/CTab.vue'
import CTabs from '~/components/shared/configurable/tab/CTabs.vue'
import { useI18n } from '~/compositions/i18n'
import { defineComponentTranslations } from '~/utils/i18n'
import { handleError } from '~/utils/http'
import { MarketplaceReview } from '~/models/marketplace/reviews'
import { useDeps } from '~/compositions/dependency-container'
import MarketplaceReviewService from '~/services/marketplace/MarketplaceReviewService'
import ScrollService from '~/services/scroll/ScrollService'
import { useSnackbar } from '~/compositions/snackbar'
import { useLogger } from '~/compositions/logger'
import { Pagination } from '~/models/search/types'
import CCard from '~/components/shared/configurable/card/CCard.vue'
import CEmptyState from '~/components/shared/configurable/CEmptyState.vue'
import CPagination from '~/components/shared/configurable/pagination/CPagination.vue'
import CAvatar from '~/components/shared/configurable/image/avatar/CAvatar.vue'
import CDirectionsButton from '~/components/shared/configurable/map/CDirectionsButton.vue'
import SellerTelephones from '~/components/car/classifieds/view/partials/seller/dealer/SellerTelephones.vue'
import SellerMiscInfo from '~/components/car/classifieds/view/partials/seller/dealer/SellerMiscInfo.vue'
import CContactForm from '~/components/shared/configurable/CContactForm.vue'
import MarketplaceSellerService from '~/services/marketplace/MarketplaceSellerService'
import { SellerReturnPolicy } from '~/models/marketplace/seller'

export default defineComponent({
  components: {
    CContactForm,
    SellerMiscInfo,
    SellerTelephones,
    CDirectionsButton,
    CAvatar,
    CPagination,
    CEmptyState,
    CCard,
    CTabs,
    CTab,
    MarketplaceReviewItem: defineVue3AsyncComponent(() =>
      import('~/components/car/marketplace/review/MarketplaceReviewItem.vue')
    )
  },
  props: {
    seller: {
      type: Object as PropType<CartSellerInfo>,
      required: true
    },
    tabAtLoad: {
      type: Number,
      required: false,
      default: 0
    },
    orderId: {
      type: Number,
      required: false,
      default: undefined
    }
  },
  setup(props) {
    const { seller, tabAtLoad } = toRefs(props)

    const { t, locale } = useI18n()
    const snackbar = useSnackbar()
    const logger = useLogger()
    const [
      marketplaceReviewService,
      marketplaceSellerService,
      scrollService
    ] = useDeps(
      MarketplaceReviewService,
      MarketplaceSellerService,
      ScrollService
    )

    const activeTab = ref(tabAtLoad.value)
    const pagination = ref<Pagination>()
    const loading = ref(false)
    const reviews = ref<MarketplaceReview[]>([])
    const returnPolicy = ref<SellerReturnPolicy>()

    onMounted(() => {
      if (activeTab.value === 1) {
        loadReviews()
      }
    })

    watch(activeTab, tabIndex => {
      const tabIsReviews = tabIndex === 1
      const tabIsPolicy = tabIndex === 2

      if (tabIsReviews) {
        loadReviews()
      } else if (tabIsPolicy) {
        loadReturnPolicy()
      }
    })

    const localeReturnPolicy = computed(() => {
      if (!returnPolicy.value) {
        return t('default_return_policy')
      }

      return returnPolicy.value[locale] || returnPolicy.value.el
    })

    const loadReviews = async (page: number = 1) => {
      loading.value = true
      reviews.value = []

      try {
        const {
          reviews: fetchedReviews
        } = await marketplaceReviewService.getReviewsForUser(
          seller.value.id,
          page,
          true
        )
        reviews.value = fetchedReviews.rows
        pagination.value = fetchedReviews.pagination
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    }

    const loadReturnPolicy = async () => {
      loading.value = true

      try {
        const data = await marketplaceSellerService.getReturnPolicies(
          seller.value.id
        )

        returnPolicy.value = data.returnPolicy
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    }

    const numberOfPages = computed(() => {
      if (pagination.value?.total) {
        return Math.ceil(pagination.value.total / pagination.value.perPage)
      }

      return 0
    })

    const handlePageChange = val => {
      loadReviews(val)
      scrollService.scrollTo() // todo add container element here
    }

    const directionsLink = computed(() => {
      if (!seller.value.geolocation) {
        if (seller.value.address) {
          return `https://maps.google.com/maps?q=${encodeURIComponent(
            seller.value.address
          )}`
        }
        return ''
      }
      return `https://www.google.com/maps/dir/?api=1&destination=${seller.value.geolocation.lat},${seller.value.geolocation.lon}`
    })

    const updateReview = (index: number, updatedReview: MarketplaceReview) => {
      vue3Set(reviews.value, index, updatedReview)
    }

    const contactUrl = computed(() => {
      return `/api/market/seller/${seller.value.id}/contact/`
    })

    return {
      activeTab,
      t,
      localeReturnPolicy,
      numberOfPages,
      handlePageChange,
      loading,
      reviews,
      pagination,
      loadReviews,
      directionsLink,
      updateReview,
      contactUrl
    }
  },
  i18n: defineComponentTranslations({
    store_details: {
      en: 'Store details',
      el: 'Πληροφορίες καταστήματος'
    },
    store_products: {
      en: 'Store products',
      el: 'Προϊόντα καταστήματος'
    },
    no_review_found_for_seller: {
      en: 'No reviews found for this store',
      el: 'Δεν βρέθηκαν αξιολογήσεις γι αυτό το κατάστημα'
    },
    default_return_policy: {
      en:
        'No return policy has been set by this seller and the general return policy applies as defined by the current legislation. In any case, Car.gr bears no responsibility for the sales of the products, contact the seller directly.',
      el:
        'Δεν ορίστηκε πολιτική επιστροφών από τον συγκεκριμένο έμπορο και ως εκ τούτου ισχύουν οι γενικοί όροι επιστροφών όπως αυτοί ορίζονται από την κείμενη νομοθεσία. Σε κάθε περίπτωση το Car.gr ουδεμία ευθύνη φέρει επί των πωλήσεων των προϊόντων, επικοινώνησε απευθείας με τον έμπορο.'
    }
  })
})
